<script lang="ts" setup>
import {
    PinturaEditor,
    PinturaEditorModal,
    PinturaEditorOverlay,
} from "@pqina/vue-pintura";
import {
    getEditorDefaults,
    type PinturaEditor as PinturaEditorObject,
    type PinturaEditorDefaultOptions,
} from "@pqina/pintura";

import { computed, ref } from "vue";

type Pintura = (
    | typeof PinturaEditor
    | typeof PinturaEditorModal
    | typeof PinturaEditorOverlay
) & {
    editor: PinturaEditorObject;
};

interface PinturaProps {
    src: string;
    show?: boolean;
    type?: "normal" | "modal" | "overlay";
    editorDefaults?: PinturaEditorDefaultOptions;
}

const props = withDefaults(defineProps<PinturaProps>(), {
    type: "normal",
    show: true,
    editorDefaults: () =>
        getEditorDefaults({
            utils: ["crop"],
        }),
});

const defaults = ref(props.editorDefaults);

const emit = defineEmits<{
    (e: "update:src", v: any): void;
    (e: "pintura:init", v: any): void;
    (e: "pintura:ready", v: any): void;
    (e: "pintura:undo", v: any): void;
    (e: "pintura:redo", v: any): void;
    (e: "pintura:hide", v: any): void;
    (e: "pintura:show", v: any): void;
    (e: "pintura:revert", v: any): void;
    (e: "pintura:destroy", v: any): void;
    (e: "pintura:close", v: any): void;
    (e: "pintura:selectutil", v: any): void;
    (e: "pintura:selectcontrol", v: any): void;
    (e: "pintura:pan", v: any): void;
    (e: "pintura:zoom", v: any): void;
    (e: "pintura:loadstart", v: any): void;
    (e: "pintura:loadabort", v: any): void;
    (e: "pintura:loaderror", v: any): void;
    (e: "pintura:loadprogress", v: any): void;
    (e: "pintura:load", v: any): void;
    (e: "pintura:loadpreview", v: any): void;
    (e: "pintura:update", v: any): void;
    (e: "pintura:processstart", v: any): void;
    (e: "pintura:processabort", v: any): void;
    (e: "pintura:processerror", v: any): void;
    (e: "pintura:processprogress", v: any): void;
    (e: "pintura:process", v: any): void;
}>();

const editor = ref<Pintura>();

const instance = computed<PinturaEditorObject>(() => {
    return editor.value?.editor;
});

const pintura = computed(() => {
    if (props.type === "modal") return PinturaEditorModal;
    else if (props.type === "overlay") return PinturaEditorOverlay;
    return PinturaEditor;
});

function onInit(event: CustomEvent<PinturaEditorObject>) {
    emit("pintura:init", {
        event: event,
        instance,
    });
}

function onReady(event: any) {
    emit("pintura:ready", {
        event,
        instance,
    });
}

function onUndo(event: any) {
    emit("pintura:undo", {
        event,
        instance,
    });
}

function onRedo(event: any) {
    emit("pintura:redo", {
        event,
        instance,
    });
}

function onHide(event: any) {
    emit("pintura:hide", {
        event,
        instance,
    });
}

function onShow(event: any) {
    if (props.type === "modal") {
        instance.value.modal.classList.remove("pointer-events-none");
    }
    emit("pintura:show", {
        event,
        instance,
    });
}

function onRevert(event: any) {
    emit("pintura:revert", {
        event,
        instance,
    });
}

function onDestroy(event: any) {
    emit("pintura:destroy", {
        event,
        instance,
    });
}

function onClose(event: any) {
    if (props.type === "modal") {
        instance.value.modal.classList.add("pointer-events-none");
    }
    emit("pintura:close", {
        event,
        instance,
    });
}

function onSelectUtil(event: any) {
    emit("pintura:selectutil", {
        event,
        instance,
    });
}

function onSelectControl(event: any) {
    emit("pintura:selectcontrol", {
        event,
        instance,
    });
}

function onPan(event: any) {
    emit("pintura:pan", {
        event,
        instance,
    });
}

function onZoom(event: any) {
    emit("pintura:zoom", {
        event,
        instance,
    });
}

function onLoadStart(event: any) {
    emit("pintura:loadstart", {
        event,
        instance,
    });
}

function onLoadAbort(event: any) {
    emit("pintura:loadabort", {
        event,
        instance,
    });
}

function onLoadError(event: any) {
    emit("pintura:loaderror", {
        event,
        instance,
    });
}

function onLoadProgress(event: any) {
    emit("pintura:loadprogress", {
        event,
        instance,
    });
}

function onLoad(event: any) {
    emit("pintura:load", {
        event,
        instance,
    });
}

function onLoadPreview(event: any) {
    emit("pintura:loadpreview", {
        event,
        instance,
    });
}

function onUpdate(event: any) {
    emit("pintura:update", {
        event,
        instance,
    });
}

function onProcessStart(event: any) {
    emit("pintura:processstart", {
        event,
        instance,
    });
}

function onProcessAbort(event: any) {
    emit("pintura:processabort", {
        event,
        instance,
    });
}

function onProcessError(event: any) {
    emit("pintura:processerror", {
        event,
        instance,
    });
}

function onProcessProgress(event: any) {
    emit("pintura:processprogress", {
        event,
        instance,
    });
}

function onProcess(event: any) {
    emit("pintura:process", {
        event,
        instance,
    });
}

function willRenderCanvas(shapes, state) {
    const { utilVisibility, selectionRect, lineColor, backgroundColor } = state;

    if (utilVisibility.crop <= 0) return shapes;

    const { x, y, width, height } = selectionRect;

    return {
        ...shapes,

        interfaceShapes: [
            {
                x: x + width * 0.5,
                y: y + height * 0.5,
                rx: width * 0.5,
                ry: height * 0.5,
                opacity: utilVisibility.crop,
                inverted: true,
                backgroundColor: [...backgroundColor, 0.5],
                strokeWidth: 1,
                strokeColor: [...lineColor],
            },
            ...shapes.interfaceShapes,
        ],
    };
}

onBeforeUnmount(() => {
    try {
        editor.value?.editor.destroy();
    } catch (e) {}
});
</script>

<template>
    <component
        :is="pintura"
        ref="editor"
        :class="{
            'pointer-events-none': props.src === undefined,
        }"
        :src="src"
        :willRenderCanvas="willRenderCanvas"
        v-bind="defaults"
        v-on:pintura:init="onInit"
        v-on:pintura:ready="onReady"
        v-on:pintura:undo="onUndo"
        v-on:pintura:redo="onRedo"
        v-on:pintura:hide="onHide"
        v-on:pintura:show="onShow"
        v-on:pintura:revert="onRevert"
        v-on:pintura:destroy="onDestroy"
        v-on:pintura:close="onClose"
        v-on:pintura:selectutil="onSelectUtil"
        v-on:pintura:selectcontrol="onSelectControl"
        v-on:pintura:pan="onPan"
        v-on:pintura:zoom="onZoom"
        v-on:pintura:loadstart="onLoadStart"
        v-on:pintura:loadabort="onLoadAbort"
        v-on:pintura:loaderror="onLoadError"
        v-on:pintura:loadprogress="onLoadProgress"
        v-on:pintura:load="onLoad"
        v-on:pintura:loadpreview="onLoadPreview"
        v-on:pintura:update="onUpdate"
        v-on:pintura:processstart="onProcessStart"
        v-on:pintura:processabort="onProcessAbort"
        v-on:pintura:processerror="onProcessError"
        v-on:pintura:processprogress="onProcessProgress"
        v-on:pintura:process="onProcess"
    />
</template>

<style>
.pintura-editor {
    --color-primary: #1d8bdf;
    --color-primary-dark: #2563eb;
    --color-primary-text: #fff;
    --color-secondary: #22c55e;
    --color-secondary-dark: #15803d;
    --color-error: 239 68 68;
    --color-focus: 29, 78, 216;
}

@media (prefers-color-scheme: dark) {
    .pintura-editor {
        --color-background: 26, 32, 46;
        --color-foreground: 229, 231, 235;
    }
}
</style>
